import type { FC } from 'react';
import {
  DevHubDialog,
  DevHubDialogTitle,
  DevHubDialogContent,
  DevHubDialogActions,
  DevHubButton,
  DevHubTypography,
  DevHubLink,
} from '@devxdiscover/devhub-ui';
import type { OwnerEligibilityTier } from '../../util/ownerValidation';

const DEVHUB_SLACK_URL = 'https://cim.slack.com/archives/C02N9B1E4QH';

export interface OwnerLevelDialogProps {
  /** Whether the dialog is open. */
  open: boolean;
  /** Display name of the selected user (e.g., "Jane Doe"). */
  userName: string;
  /** Job title of the selected user (e.g., "SVP Engineering"). */
  userTitle: string;
  /** Eligibility tier — controls the message variant and available actions. */
  tier: OwnerEligibilityTier;
  /** Called when the dialog is dismissed (OK or Cancel). */
  onClose: () => void;
  /** Called when the warn-tier user clicks "Assign Anyway". */
  onConfirm: () => void;
}

/**
 * Dialog shown when a selected owner falls within a restricted employee
 * level tier. The exclude tier blocks assignment; the warn tier asks for
 * confirmation.
 *
 * @param props - {@link OwnerLevelDialogProps}
 */
export const OwnerLevelDialog: FC<OwnerLevelDialogProps> = ({
  open,
  userName,
  userTitle,
  tier,
  onClose,
  onConfirm,
}) => {
  if (tier === 'allow') {
    return null;
  }

  const nameLabel = userTitle ? `${userName} (${userTitle})` : userName;

  const isExclude = tier === 'exclude';

  const title = isExclude
    ? 'Owner Assignment Blocked'
    : 'Confirm Owner Assignment';

  const message = isExclude
    ? `${nameLabel} may hold a senior position. Application owners receive` +
      ' operational communications that are typically outside the scope' +
      ' of senior leadership roles.'
    : `Are you sure? ${nameLabel} holds a senior position. Application` +
      ' owners receive operational communications that are typically' +
      ' outside the scope of senior leadership roles.';

  return (
    <DevHubDialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DevHubDialogTitle>{title}</DevHubDialogTitle>

      <DevHubDialogContent>
        <DevHubTypography variant="body1" style={{ marginBottom: '1rem' }}>
          {message}
        </DevHubTypography>

        <DevHubTypography variant="body2">
          For questions, contact{' '}
          <DevHubLink href={DEVHUB_SLACK_URL} target="_blank" rel="noopener">
            #devhub
          </DevHubLink>{' '}
          on Slack.
        </DevHubTypography>
      </DevHubDialogContent>

      <DevHubDialogActions>
        {isExclude ? (
          <DevHubButton onClick={onClose} variant="contained" color="primary">
            OK
          </DevHubButton>
        ) : (
          <>
            <DevHubButton onClick={onClose} variant="outlined">
              Cancel
            </DevHubButton>
            <DevHubButton
              onClick={onConfirm}
              variant="contained"
              color="primary"
            >
              Assign Anyway
            </DevHubButton>
          </>
        )}
      </DevHubDialogActions>
    </DevHubDialog>
  );
};
